import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, Slide, IconButton, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close as CloseIcon, DragIndicator as DragIcon, OpenInFull, CloseFullscreen } from '@mui/icons-material';
import { useWebSocket, useChatSession } from './hooks';
import { MessageList, MessageInput, ConnectionStatus } from './components';
import { CHAT_API_BASE_URL_READ, getChatAccessToken } from './config';
import type { ChatMessage, ChatMessageType } from './types';
import { ConsentConfirmationModal } from '../PrivacyPolicySetting';

import { useModal } from '../../hooks/useModal';
import { useLazyGetUserConsentsQuery } from '../../redux/apis/consents.api';
import './FloatingChatWidget.css';
import CustomTooltip from '../../components/CustomTooltip';
import { findAiConsent } from '../../features/PrivacyPolicySetting/utils/findAiConsent';

type FloatingChatWidgetProps = {
  isProd: boolean;
};

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_WIDGET_WIDTH = 320;
const MIN_WIDGET_HEIGHT = 360;
const AI_PILL_EXPAND_MS = 300;

const AI_LABEL_SX = {
  fontSize: { xs: '8px', sm: '11px' },
  fontWeight: 600,
  lineHeight: 1,
  color: '#fff',
  whiteSpace: 'nowrap',
  display: 'block',
  m: 0,
} as const;

const AI_SUBLABEL_SX = {
  fontSize: { xs: '6px', sm: '7px' },
  fontWeight: 900,
  lineHeight: 1,
  color: '#6366F1',
  whiteSpace: 'nowrap',
  display: { xs: 'block', xl: 'none' },
  mt: '3px',
  textAlign: 'center',
} as const;

const Sparkle = ({ delay, className }: { delay: number; className?: string }) => {
  return <div className={`sparkle ${className ?? ''}`} style={{ animationDelay: `${delay}s` }} />;
};
const Sparkle1 = ({ delay, className }: { delay: number; className?: string }) => {
  return <div className={`sparkle1 ${className ?? ''}`} style={{ animationDelay: `${delay}s` }} />;
};

export default function FloatingChatWidget({ isProd }: FloatingChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 470, y: 20 });
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [showExpandedLabel, setShowExpandedLabel] = useState(false);
  const expandLabelTimeoutRef = useRef<number | null>(null);
  const blockHoverFromTouchRef = useRef(false);
  const theme = useTheme();
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const isLgUp = useMediaQuery(theme.breakpoints.up('md'));
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    direction: 'se' as ResizeDirection,
    mouseX: 0,
    mouseY: 0,
    x: 0,
    y: 0,
    width: 400,
    height: 600,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const preMaximizeStateRef = useRef({ position: { x: window.innerWidth - 470, y: 20 }, size: { width: 400, height: 600 } });

  const { sessionId, loading: sessionLoading } = useChatSession();
  const { connect, sendMessage, isConnected, connectionError } = useWebSocket(sessionId);
  const connectedSessionIdRef = useRef<string | null>(null);
  const hasConnectedRef = useRef<boolean>(false);
  const pendingWidgetOpenRef = useRef<'main' | 'chat'>('main');

  const openWidgetAfterConsent = useCallback(() => {
    if (pendingWidgetOpenRef.current === 'chat') {
      setIsChatOpen(true);
      setIsMinimized(false);
      return;
    }
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const { isModalOpen: isAiConsentModalOpen, openModal: openAiConsentModal, closeModal: closeAiConsentModal } = useModal();
  const [getUserConsents] = useLazyGetUserConsentsQuery();

  const handleAiButtonClick = useCallback(async () => {
    try {
      const response = await getUserConsents().unwrap();
      const aiConsent = findAiConsent(response?.consents);

      if (aiConsent?.status === 'ACCEPTED') {
        openWidgetAfterConsent();
      } else {
        openAiConsentModal();
      }
    } catch (err) {
      console.error('Failed to fetch user consents:', err);
    }
  }, [getUserConsents, openAiConsentModal, openWidgetAfterConsent]);

  const [fabPosition, setFabPosition] = useState({
    x: window.innerWidth - 50,
    y: window.innerHeight - 200,
  });
  const [isFabDragging, setIsFabDragging] = useState(false);
  const [fabDragStart, setFabDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);

  const handleFabMouseDown = (e: React.MouseEvent) => {
    setIsFabDragging(true);
    setHasMoved(false);
    setFabDragStart({
      x: e.clientX - fabPosition.x,
      y: e.clientY - fabPosition.y,
    });
  };

  const handleToggleOne = () => {
    if (isChatOpen) {
      setIsChatOpen(false);
      return;
    }
    pendingWidgetOpenRef.current = 'chat';
    handleAiButtonClick();
  };

  const handleFabClick = () => {
    if (hasMoved) return; // prevent click when dragging
    handleToggleOne(); // or handleToggle based on your condition
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isFabDragging) return;

      const newX = e.clientX - fabDragStart.x;
      const newY = e.clientY - fabDragStart.y;

      // detect movement (to prevent click trigger)
      if (Math.abs(e.clientX - fabDragStart.x) > 5 || Math.abs(e.clientY - fabDragStart.y) > 5) {
        setHasMoved(true);
      }

      setFabPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 60)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 60)),
      });
    };

    const handleMouseUp = () => {
      setIsFabDragging(false);
      setTimeout(() => setHasMoved(false), 0);
    };

    if (isFabDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isFabDragging, fabDragStart]);

  const loadMessagesFromSession = useCallback(async (sessionIdToLoad: string) => {
    const token = getChatAccessToken();
    if (!token) return;
    try {
      const response = await fetch(`${CHAT_API_BASE_URL_READ}/chat/session/${sessionIdToLoad}/messages?limit=50&offset=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const loadedMessages = (data.messages ?? [])
          .reverse()
          .filter((msg: { role?: string }) => msg.role !== 'system')
          .map(
            (msg: { id?: unknown; message_id?: unknown; role: string; content?: string; created_at?: string; message_type?: string }) => ({
              id: String(msg.id ?? msg.message_id ?? ''),
              role: msg.role as 'user' | 'assistant',
              content: msg.content || '',
              timestamp: new Date(msg.created_at || Date.now()),
              message_type: (msg.message_type === 'greeting'
                ? 'greeting'
                : msg.message_type === 'download'
                  ? 'download'
                  : 'text') as ChatMessageType,
              download_url: (msg as { download_url?: string }).download_url,
            })
          );
        setMessages(loadedMessages);
      }
    } catch (err) {
      console.error('[FloatingChatWidget] Failed to load messages:', err);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadMessagesFromSession(sessionId);
    } else {
      setMessages([]);
    }
  }, [sessionId, loadMessagesFromSession]);

  useEffect(() => {
    if (sessionId && !sessionLoading) {
      if (connectedSessionIdRef.current !== sessionId && !hasConnectedRef.current) {
        connectedSessionIdRef.current = sessionId;
        hasConnectedRef.current = true;
        const timeoutId = setTimeout(() => connect(sessionId), 100);
        return () => clearTimeout(timeoutId);
      } else if (connectedSessionIdRef.current !== sessionId) {
        connectedSessionIdRef.current = sessionId;
      }
    } else if (!sessionId) {
      connectedSessionIdRef.current = null;
      hasConnectedRef.current = false;
    }
  }, [sessionId, sessionLoading, connect]);

  useEffect(() => {
    if (connectionError) setIsLoading(false);
  }, [connectionError]);

  useEffect(() => {
    const handleChatMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{
        type: string;
        sender?: string;
        message?: string;
        timestamp?: string;
        message_type?: string;
        download_url?: string;
        download_urls?: string[];
        balance_suggestions?: ChatMessage['balance_suggestions'];
      }>;
      const data = customEvent.detail;
      if (data.type === 'message' && data.sender === 'bot' && data.message !== undefined) {
        setIsLoading(false);
        const botMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(data.timestamp || Date.now()),
          message_type: (data.message_type === 'greeting'
            ? 'greeting'
            : data.message_type === 'download'
              ? 'download'
              : 'text') as ChatMessageType,
          download_url: data.download_url,
          download_urls: data.download_urls,
          balance_suggestions: data.balance_suggestions,
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    };

    const handleSystemMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; message?: string; content?: string; timestamp?: string }>;
      const data = customEvent.detail;
      if (data.type === 'system' || data.type === 'connection') {
        const systemMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'system',
          content: data.message || data.content || 'Connected',
          timestamp: new Date(data.timestamp || Date.now()),
        };
        setMessages((prev) => [...prev, systemMessage]);
      }
    };

    window.addEventListener('chat-message', handleChatMessage);
    window.addEventListener('system-message', handleSystemMessage);
    return () => {
      window.removeEventListener('chat-message', handleChatMessage);
      window.removeEventListener('system-message', handleSystemMessage);
    };
  }, []);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || !isConnected || !sessionId) return;
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      sendMessage(content.trim());
    },
    [isConnected, sessionId, sendMessage]
  );

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    pendingWidgetOpenRef.current = 'main';
    handleAiButtonClick();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMaximized) return;
    if (e.target === dragRef.current || dragRef.current?.contains(e.target as Node)) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && !isResizing) {
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || 400;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || (isMinimized ? 60 : 600);
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition({
        x: Math.max(0, Math.min(e.clientX - dragStart.x, maxX)),
        y: Math.max(0, Math.min(e.clientY - dragStart.y, maxY)),
      });
    }
  };

  const handleResizeMouseDown = (direction: ResizeDirection, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMinimized || isMaximized) return;
    setIsResizing(true);
    setResizeStart({
      direction,
      mouseX: e.clientX,
      mouseY: e.clientY,
      x: position.x,
      y: position.y,
      width: chatRef.current?.getBoundingClientRect().width || size.width,
      height: chatRef.current?.getBoundingClientRect().height || size.height,
    });
  };

  const handleResizeMouseMove = (e: MouseEvent) => {
    if (!isResizing || isMinimized) return;

    const dx = e.clientX - resizeStart.mouseX;
    const dy = e.clientY - resizeStart.mouseY;
    const maxBottom = window.innerHeight - 70;

    let nextX = resizeStart.x;
    let nextY = resizeStart.y;
    let nextWidth = resizeStart.width;
    let nextHeight = resizeStart.height;

    if (resizeStart.direction.includes('e')) nextWidth = resizeStart.width + dx;
    if (resizeStart.direction.includes('w')) {
      nextWidth = resizeStart.width - dx;
      nextX = resizeStart.x + dx;
    }
    if (resizeStart.direction.includes('s')) nextHeight = resizeStart.height + dy;
    if (resizeStart.direction.includes('n')) {
      nextHeight = resizeStart.height - dy;
      nextY = resizeStart.y + dy;
    }

    if (nextWidth < MIN_WIDGET_WIDTH) {
      if (resizeStart.direction.includes('w')) {
        nextX -= MIN_WIDGET_WIDTH - nextWidth;
      }
      nextWidth = MIN_WIDGET_WIDTH;
    }
    if (nextHeight < MIN_WIDGET_HEIGHT) {
      if (resizeStart.direction.includes('n')) {
        nextY -= MIN_WIDGET_HEIGHT - nextHeight;
      }
      nextHeight = MIN_WIDGET_HEIGHT;
    }

    if (nextX < 0) {
      nextWidth += nextX;
      nextX = 0;
    }
    if (nextY < 0) {
      nextHeight += nextY;
      nextY = 0;
    }

    if (nextX + nextWidth > window.innerWidth) {
      nextWidth = window.innerWidth - nextX;
    }
    if (nextY + nextHeight > maxBottom) {
      nextHeight = maxBottom - nextY;
    }

    nextWidth = Math.max(MIN_WIDGET_WIDTH, nextWidth);
    nextHeight = Math.max(MIN_WIDGET_HEIGHT, nextHeight);

    setPosition({ x: nextX, y: nextY });
    setSize({ width: nextWidth, height: nextHeight });
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleResizeMouseUp = () => setIsResizing(false);

  const handleToggleMaximize = () => {
    if (!isMaximized) {
      preMaximizeStateRef.current = { position: { ...position }, size: { ...size } };
      setIsMinimized(false);
      setIsMaximized(true);
      setPosition({ x: 0, y: 0 });
      setSize({ width: window.innerWidth, height: window.innerHeight });
    } else {
      setIsMaximized(false);
      setPosition(preMaximizeStateRef.current.position);
      setSize(preMaximizeStateRef.current.size);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [isResizing, resizeStart, isMinimized]);

  useEffect(() => {
    const handleResize = () => {
      if (isMaximized) {
        setPosition({ x: 0, y: 0 });
        setSize({ width: window.innerWidth, height: window.innerHeight });
        return;
      }
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || size.width;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || (isMinimized ? 60 : size.height);
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));

      setSize((prev) => ({
        width: Math.min(prev.width, window.innerWidth),
        height: Math.min(prev.height, Math.max(MIN_WIDGET_HEIGHT, window.innerHeight - 70)),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMinimized, size.width, size.height, isMaximized]);

  const clearExpandLabelTimeout = useCallback(() => {
    if (expandLabelTimeoutRef.current !== null) {
      window.clearTimeout(expandLabelTimeoutRef.current);
      expandLabelTimeoutRef.current = null;
    }
  }, []);

  const handleAiMouseEnter = useCallback(() => {
    if (!canHover || blockHoverFromTouchRef.current) return;

    clearExpandLabelTimeout();
    setAiExpanded(true);
    expandLabelTimeoutRef.current = window.setTimeout(() => {
      setShowExpandedLabel(true);
      expandLabelTimeoutRef.current = null;
    }, AI_PILL_EXPAND_MS);
  }, [canHover, clearExpandLabelTimeout]);

  const handleAiMouseLeave = useCallback(() => {
    if (!canHover || blockHoverFromTouchRef.current) return;

    clearExpandLabelTimeout();
    setShowExpandedLabel(false);
    setAiExpanded(false);
  }, [canHover, clearExpandLabelTimeout]);

  const handleAiTouchStart = useCallback(() => {
    blockHoverFromTouchRef.current = true;
    clearExpandLabelTimeout();
    setShowExpandedLabel(false);
    setAiExpanded(false);

    window.setTimeout(() => {
      blockHoverFromTouchRef.current = false;
    }, 400);
  }, [clearExpandLabelTimeout]);

  const handleAiTrigger = isProd ? handleFabClick : handleToggle;

  useEffect(() => clearExpandLabelTimeout, [clearExpandLabelTimeout]);

  // lg+: shrink Ask pill to sparkles-only while scrolled; expand again at top
  useEffect(() => {
    if (!isLgUp) {
      setIsScrolledDown(false);
      return;
    }

    const updateScrollState = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      setIsScrolledDown(scrollTop > 16);
    };

    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollState);
  }, [isLgUp]);

  const isCompactAiButton = isLgUp && isScrolledDown;

  // Keep widget in-bounds after manual resize.
  useEffect(() => {
    if (!chatRef.current || !isOpen || isMinimized) return;
    const observer = new ResizeObserver(() => {
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || 400;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || 600;
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    });
    observer.observe(chatRef.current);
    return () => observer.disconnect();
  }, [isOpen, isMinimized]);

  return (
    <>
      <>
<Box
  sx={{
    position: { md: 'fixed' },
    marginRight: { xs: '10px', sm: '15px', md: 0 },
    top: { md: 52 },
    right: { sm: 10 },
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    outline: 'none',
    userSelect: 'none',
  }}
  onClick={handleAiTrigger}
  onTouchStart={handleAiTouchStart}
  onMouseEnter={handleAiMouseEnter}
  onMouseLeave={handleAiMouseLeave}
>
  <Box
    sx={{
      width: isCompactAiButton ? { xs: 68, sm: 28 } : { xs: 68, sm: 100 },
      height: { xs: 25, sm: 25 },
      p: '1px',
      borderRadius: '999px',
      position: 'relative',
      overflow: 'hidden',

      background:
        'linear-gradient(90deg,#7C3AED 0%,#A855F7 40%,#3B82F6 72%,#06B6D4 100%)',

      border: '1px solid rgba(255,255,255,.25)',

      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',

      boxShadow: `
        0 6px 18px rgba(124,58,237,.28),
        inset 0 1px 0 rgba(255,255,255,.25)
      `,

      transition: 'width .28s ease, transform .25s ease, box-shadow .25s ease',

      '&:hover': {
        transform: 'translateY(-1px) scale(1.03)',
        boxShadow: `
          0 10px 24px rgba(124,58,237,.35),
          inset 0 1px 0 rgba(255,255,255,.3)
        `,
      },

      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 2,
        borderRadius: '999px',
        border: '1px solid rgba(255,255,255,.15)',
      },

      '&::after': {
        content: '""',
        position: 'absolute',
        top: 3,
        left: '12%',
        width: '76%',
        height: '38%',
        borderRadius: '999px',
        background:
          'linear-gradient(180deg, rgba(255,255,255,.32), rgba(255,255,255,0))',
        filter: 'blur(3px)',
      },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: isCompactAiButton ? 0 : { xs: 0.6, sm: 1 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {isCompactAiButton ? (
          <Box
            component="img"
            src="/theXLogo.png"
            alt="theX"
            sx={{
              height: { xs: 22, sm: 24 },
              width: 'auto',
              display: 'block',
              objectFit: 'contain',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.25))',
            }}
          />
        ) : (
          <>
            {/* Sparkles */}
            <Box
              sx={{
                position: 'relative',
                width: { xs: 11, sm: 16 },
                height: { xs: 9, sm: 12 },
                mr: { xs: 0.4, sm: 0.7 },
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkle1 delay={0} className="sparkle1-small-top" />
              <Sparkle1 delay={0.5} className="sparkle1-small-bottom" />
              <Sparkle1 delay={1} className="sparkle1-large" />
            </Box>

            {/* Divider */}
            <Box
              sx={{
                width: '1px',
                height: { xs: 10, sm: 14 },
                bgcolor: 'rgba(255,255,255,.45)',
                mr: { xs: 0.5, sm: 0.8 },
                flexShrink: 0,
                alignSelf: 'center',
              }}
            />

            {/* Ask + Logo */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
              }}
            >
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: {
                    xs: '8px',
                    sm: '11px',
                  },
                  letterSpacing: '.2px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  m: 0,
                  p: 0,
                }}
              >
                Ask
              </Typography>

              <Box
                component="img"
                src="/theXLogo.png"
                alt="theX"
                sx={{
                  height: {
                    xs: 25,
                    sm: 30,
                  },
                  width: 'auto',
                  ml: { xs: '-1px', md: '-5px' },
                  mt: { xs: '-1px', md: '0px' },
                  display: 'block',
                  objectFit: 'contain',
                  alignSelf: 'center',
                  filter: `
                    drop-shadow(0 1px 2px rgba(0,0,0,.25))
                  `,
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  </Box>
</Box>
      </>

      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          ref={chatRef}
          elevation={8}
          sx={{
            position: 'fixed',

            left: isMaximized ? { xs: 10, md: 0 } : position.x + 15,

            top: isMaximized ? { xs: 10, md: 0 } : position.y,

            width: isMaximized ? { xs: '95vw', sm: '100vw' } : size.width,

            height: isMinimized
              ? 'auto'
              : isMaximized
                ? {
                    xs: 'calc(100vh - 20px)',
                    md: '100vh',
                  }
                : size.height,

            maxWidth: isMaximized ? '100vw' : { xs: '70vw', md: '99vw' },

            maxHeight: isMinimized ? 'none' : isMaximized ? '100vh' : { xs: '70vh', md: '99vh' },

            zIndex: 1301,

            borderRadius: isMaximized ? 0 : 2,

            overflow: 'hidden',

            display: 'flex',
            flexDirection: 'column',

            backgroundColor: '#ffffff',
          }}
          onMouseDown={handleMouseDown}
        >
          <Box
            ref={dragRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'move',
              minHeight: '48px',
              '&:hover': { backgroundColor: '#e8e8e8' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <DragIcon sx={{ color: '#666666', fontSize: 18, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ConnectionStatus isConnected={isConnected} error={connectionError} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMinimized ? '□' : '—'}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleMaximize}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMaximized ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {!isMinimized && (
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                backgroundColor: '#efeae2',
              }}
            >
              {sessionLoading && !sessionId ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666666' }}>Creating chat session...</Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      maxHeight: '100%',
                      backgroundColor: '#efeae2',
                    }}
                  >
                    <MessageList messages={messages} isLoading={isLoading} onQuickActionClick={handleSendMessage} />
                  </Box>
                  <MessageInput onSendMessage={handleSendMessage} disabled={!isConnected || !sessionId} />
                </>
              )}
            </Box>
          )}

          {!isMinimized && !isMaximized && (
            <>
              <Box
                onMouseDown={(e) => handleResizeMouseDown('n', e)}
                sx={{ position: 'absolute', top: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('s', e)}
                sx={{ position: 'absolute', bottom: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('w', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, left: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('e', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, right: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('nw', e)}
                sx={{ position: 'absolute', top: 0, left: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('ne', e)}
                sx={{ position: 'absolute', top: 0, right: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('sw', e)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('se', e)}
                sx={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
            </>
          )}
        </Paper>
      </Slide>

      <Slide direction="up" in={isChatOpen} mountOnEnter unmountOnExit>
        <Paper
          ref={chatRef}
          elevation={8}
          sx={{
            position: 'fixed',
            left: isMaximized ? 0 : position.x,
            top: isMaximized ? 0 : position.y,
            width: isMaximized ? '100vw' : size.width,
            height: isMinimized ? 'auto' : isMaximized ? '100vh' : size.height,
            maxWidth: isMaximized ? '100vw' : { xs: '70vw', md: '99vw' },
            maxHeight: isMinimized ? 'none' : isMaximized ? '100vh' : { xs: '70vh', md: '99vh' },
            zIndex: 1301,
            borderRadius: isMaximized ? 0 : 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
          }}
          onMouseDown={handleMouseDown}
        >
          <Box
            ref={dragRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'move',
              minHeight: '48px',
              '&:hover': { backgroundColor: '#e8e8e8' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <DragIcon sx={{ color: '#666666', fontSize: 18, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ConnectionStatus isConnected={false} error={connectionError} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMinimized ? '□' : '—'}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleMaximize}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMaximized ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleOne}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {!isMinimized && (
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                backgroundColor: '#efeae2',
              }}
            >
              {sessionLoading && !sessionId ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666666' }}>Creating chat session...</Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      textAlign: 'center',
                      backgroundColor: '#efeae2',
                      color: '#555',
                      fontSize: '14px',
                      fontWeight: 500,
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="img"
                      src="/LX_Genie_02 (1).gif" // your SVG file
                      alt="animation"
                      sx={{
                        position: 'absolute',
                        width: '60%',
                        opacity: 0.2, // subtle effect
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 2, color: '#555' }}>
                      <span style={{ fontSize: '16px', fontWeight: 600 }}>🚀 Rolling out soon</span>
                      <span style={{ fontSize: '12px', marginTop: '6px', color: '#888', display: 'block' }}>
                        This feature will be available shortly
                      </span>
                    </Box>
                  </Box>
                  <MessageInput onSendMessage={handleSendMessage} disabled={true} />
                </>
              )}
            </Box>
          )}

          {!isMinimized && !isMaximized && (
            <>
              <Box
                onMouseDown={(e) => handleResizeMouseDown('n', e)}
                sx={{ position: 'absolute', top: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('s', e)}
                sx={{ position: 'absolute', bottom: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('w', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, left: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('e', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, right: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('nw', e)}
                sx={{ position: 'absolute', top: 0, left: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('ne', e)}
                sx={{ position: 'absolute', top: 0, right: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('sw', e)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('se', e)}
                sx={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
            </>
          )}
        </Paper>
      </Slide>

      {isAiConsentModalOpen ? (
        <ConsentConfirmationModal
          open={isAiConsentModalOpen}
          onClose={closeAiConsentModal}
          onAccepted={() => {
            openWidgetAfterConsent();
            closeAiConsentModal();
          }}
        />
      ) : null}
    </>
  );
}
